import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { filter, shareReplay, take } from 'rxjs/operators';

import { IntellectualChildrenValidity } from '@common/entities/dialog/invalid-intellectual-children-dialog.entity';
import {
    SelectZoneClassificationDialogOutput,
    SelectZoneClassificationDialogOutputSuccess
} from '@common/entities/dialog/select-classification-dialog.entity';

import {
    SelectRecordTypeDialogOutput,
    SelectRecordTypeDialogOutputSuccess
} from '@common/entities/dialog/select-record-type-dialog.entity';

import {
    SelectParentDialogOutput,
    SelectParentDialogOutputSuccess,
    SelectParentDialogType
} from '@common/entities/dialog/select-parent-dialog.entity';

import {
    BatchTagDialogComponent,
    BatchTagDialogResult
} from '@common/modules/dialog/batch-tag-dialog/batch-tag-dialog.component';
import { SharedLanguageService } from '@language';
import {
    ApiFragment,
    DialogHelperBase,
    DialogRef,
    DialogStyle,
    Identifier,
    RecordId,
    RecordType,
    RecordTypeStructure,
    ShDynamicDialogService,
    ZoneId
} from '@stylehaven/components';
import { InvalidIntellectualChildrenDialogComponent } from './invalid-intellectual-children-dialog/invalid-intellectual-children-dialog.component';
import { SelectParentDialogComponent } from './select-parent-dialog/select-parent-dialog.component';
import { SelectRecordTypeDialogComponent } from './select-record-type-dialog/select-record-type-dialog.component';
import { SelectZoneClassificationDialogComponent } from './select-zone-classification-dialog/select-zone-classification-dialog.component';
import { UnpublishedParentsDialogComponent } from './unpublished-parents-dialog/unpublished-parents-dialog.component';

export interface ConfirmationDialogConfig {
    title: string;
    errorMessage?: string;
    warningMessage?: string;
    infoMessage?: string;
    message: string;
    yes: string;
    no: string;
}

@Injectable({ providedIn: 'root' })
export class DialogHelper extends DialogHelperBase {
    constructor(
        public dialogService: ShDynamicDialogService,
        public translateService: SharedLanguageService
    ) {
        super(dialogService, translateService);
    }

    public invalidIntellectualRecordChildren(parents: ApiFragment[], invalidChildrenIds: Identifier[][]): void {
        const type: string = parents.every(parent => parent.isNewCollection()) ? 'collection' : 'generic';
        const title: string = this.translateService.translate(`intellectual.invalid-children-dialog.title.${type}`);
        const cancel: string = this.translateService.translate('actions.common.cancel');

        const nrOfInvalidChildren: number[] = invalidChildrenIds.map(ids => ids.length);

        const intellectualRecordValidityObjects: IntellectualChildrenValidity[] = parents.map(
            (intellectualRecord, index) => ({
                intellectualRecord,
                nrOfInvalidChildren: nrOfInvalidChildren[index]
            })
        );

        this.dialogService.open(InvalidIntellectualChildrenDialogComponent, {
            title,
            data: { cancel, intellectualRecordValidityObjects }
        });
    }

    public unpublishedParents(children: ApiFragment[], unpublishedParents: ApiFragment[]) {
        const title: string = this.translateService.translate(`items.unpublished-parent-dialog.title`);
        const cancel: string = this.translateService.translate('actions.common.cancel');

        this.dialogService.open(UnpublishedParentsDialogComponent, {
            title,
            data: { cancel, children, unpublishedParents }
        });
    }

    public recordTypeSelectionDialog(
        recordTypeContext?: RecordType,
        allowedStructures?: RecordTypeStructure[]
    ): Observable<SelectRecordTypeDialogOutputSuccess> {
        const title: string = this.translateService.translate('creation-flow.record-type-dialog.title');

        const yes: string = this.translateService.translate('actions.next');
        const no: string = this.translateService.translate('actions.common.cancel');

        const ref: DialogRef<SelectRecordTypeDialogOutput> = this.dialogService.open(SelectRecordTypeDialogComponent, {
            title,
            data: { yes, no, recordTypeContext, allowedStructures }
        });

        return ref.afterClosed$.pipe(
            filter(
                (output: SelectRecordTypeDialogOutput): output is SelectRecordTypeDialogOutputSuccess =>
                    output.type === 'success' && output.hasOwnProperty('recordType')
            ),
            shareReplay(1)
        );
    }

    public selectParentDialog(
        dialogType: SelectParentDialogType,
        recordTypes?: RecordType[],
        classificationId?: Identifier,
        recordsToOmit?: RecordId[]
    ): Observable<SelectParentDialogOutputSuccess> {
        const title: string = this.translateService.translate('creation-flow.parent-select-dialog.title');

        const yes: string = this.translateService.translate('actions.next');
        const no: string = this.translateService.translate('actions.common.cancel');

        const ref: DialogRef<SelectParentDialogOutput> = this.dialogService.open(SelectParentDialogComponent, {
            title,
            data: { yes, no, recordTypes, classificationId, recordsToOmit, dialogType }
        });

        return ref.afterClosed$.pipe(
            filter(
                (output: SelectParentDialogOutput): output is SelectParentDialogOutputSuccess =>
                    output.type === 'success' && output.hasOwnProperty('parentRecord')
            ),
            shareReplay(1)
        );
    }

    public zoneClassificationDialog(
        classificationId?: string | null,
        parentZoneId?: ZoneId | null
    ): Observable<SelectZoneClassificationDialogOutputSuccess> {
        const title: string = this.translateService.translate('classifications.zone-classifications-dialog.title');

        const yes: string = this.translateService.translate('actions.next');
        const no: string = this.translateService.translate('actions.common.cancel');

        const ref: DialogRef<SelectZoneClassificationDialogOutput> = this.dialogService.open(
            SelectZoneClassificationDialogComponent,
            {
                title,
                data: { yes, no, classificationId, parentZoneId }
            }
        );

        return ref.afterClosed$.pipe(
            filter(
                (output: SelectZoneClassificationDialogOutput): output is SelectZoneClassificationDialogOutputSuccess =>
                    output.type === 'success' &&
                    output.hasOwnProperty('classificationId') &&
                    output.hasOwnProperty('zoneId')
            ),
            shareReplay(1)
        );
    }

    public batchWarningDialog(): Observable<boolean> {
        const yes: string = this.translateService.translate('actions.start-batch');
        const no: string = this.translateService.translate('actions.common.cancel');
        const title: string = this.translateService.translate('batches.start-batch-dialog.title');
        const message: string = this.translateService.translate('batches.start-batch-dialog.message');

        return this.confirm({ title, message, yes, no }, DialogStyle.Wide).pipe(take(1), shareReplay(1));
    }

    public batchWarningAndTagDialog() {
        const yes: string = this.translateService.translate('actions.start-batch');
        const no: string = this.translateService.translate('actions.common.cancel');
        const title: string = this.translateService.translate('batches.start-batch-dialog.title');

        return this.dialogService.open<BatchTagDialogResult>(BatchTagDialogComponent, {
            title,
            options: {
                style: DialogStyle.Wide
            },
            data: {
                yes,
                no
            }
        }).afterClosed$;
    }
}
